import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";

/**
 * ForgotPassword — single email input, fire-and-forget request.
 * We always show the same confirmation regardless of whether the
 * email exists (account-enumeration prevention); the backend does
 * the heavy lifting.
 */
export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim() });
        } catch {
            // Never surface errors — backend is permissive by design.
        } finally {
            setBusy(false);
            setSent(true);
        }
    };

    return (
        <div className="grain min-h-screen candle-halo" data-testid="forgot-password">
            <Header minimal />
            <div className="max-w-md mx-auto px-6 pt-16 pb-24 animate-fade-in">
                <div className="flex items-center gap-3 mb-6">
                    <BrandMark size={26} />
                    <p className="label-eyebrow">Recupero password</p>
                </div>

                {!sent ? (
                    <>
                        <h1 className="h-serif text-4xl md:text-5xl mb-6 leading-tight">
                            Dimenticare <span className="italic text-ink-gold">capita.</span>
                        </h1>
                        <p className="text-ink-textMid leading-relaxed mb-10">
                            Inserisci la tua email: ti invieremo un link per scegliere una nuova password.
                            Il link sarà valido per un'ora.
                        </p>

                        <form onSubmit={submit} className="space-y-7">
                            <div>
                                <label className="label-eyebrow" htmlFor="email">Email</label>
                                <input
                                    id="email"
                                    type="email"
                                    data-testid="forgot-email"
                                    className="input-line mt-2"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoFocus
                                />
                            </div>
                            <button
                                disabled={busy}
                                data-testid="forgot-submit"
                                className="btn-primary w-full"
                            >
                                {busy ? "…" : "Invia il link"}
                            </button>
                        </form>
                    </>
                ) : (
                    <div data-testid="forgot-sent">
                        <h1 className="h-serif text-4xl md:text-5xl mb-6 leading-tight">
                            Controlla <span className="italic text-ink-gold">la posta.</span>
                        </h1>
                        <p className="text-ink-textMid leading-relaxed mb-4">
                            Se esiste un account associato a quell'email, tra qualche istante riceverai
                            un messaggio con il link per reimpostare la password.
                        </p>
                        <p className="text-sm text-ink-textLow font-serif italic leading-relaxed mb-10">
                            Il link è valido per un'ora. Controlla anche la cartella spam —
                            a volte i messaggi importanti si nascondono lì.
                        </p>
                        <Link to="/login" className="btn-secondary inline-block" data-testid="forgot-back-to-login">
                            Torna al login
                        </Link>
                    </div>
                )}

                <div className="mt-10 pt-6 border-t border-ink-border text-center text-sm text-ink-textMid">
                    <Link to="/login" className="hover:text-ink-textHigh underline underline-offset-4 decoration-ink-gold/40">
                        Ricordi la password?
                    </Link>
                </div>
            </div>
        </div>
    );
}
